import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import PartnerHospitalSelect from '@/components/partner/PartnerHospitalSelect';
import PartnerMilestoneRatesCard, {
  type PartnerMilestoneRatesRow,
} from '@/components/partner/PartnerMilestoneRatesCard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function PartnerPoints() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hospitalId, setHospitalId] = useState('');

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['partner-points'],
    queryFn: async () => {
      const res = await referralPartnerApi.points();
      return res.data?.data;
    },
  });

  const { data: profileData } = useQuery({
    queryKey: ['partner-me'],
    queryFn: async () => {
      const res = await referralPartnerApi.me();
      return res.data?.data;
    },
  });
  const milestoneRates = (profileData?.milestoneRates ?? []) as PartnerMilestoneRatesRow[];

  const summary = data?.summary;
  const entries = (data?.entries ?? []) as Array<{
    id: string;
    milestone: string;
    points: number;
    status: string;
    hospital: { id: string; name: string };
    lead: { patientName: string };
    payoutLine?: { payout: { status: string } };
  }>;

  const payable = entries.filter(
    (e) => e.status === 'APPROVED' && !e.payoutLine
  );

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const requestPayout = async () => {
    if (!hospitalId) {
      toast.error('Select a hospital for this payout');
      return;
    }
    const ledgerIds = [...selected];
    if (!ledgerIds.length) {
      toast.error('Select points entries');
      return;
    }
    try {
      await referralPartnerApi.requestPayout({ hospitalId, ledgerIds });
      toast.success('Payout request submitted');
      setSelected(new Set());
      refetch();
      navigate('/partner/payouts');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Request failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Points</h2>
        <p className="text-slate-400">
          Available for payout: <strong className="text-cyan-400">{summary?.availableForPayout ?? 0}</strong> pts
        </p>
      </div>

      {milestoneRates.length > 0 && <PartnerMilestoneRatesCard rates={milestoneRates} compact />}

      {payable.length > 0 && (
        <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4 space-y-3">
          <p className="text-sm text-cyan-100">Request settlement for approved entries</p>
          <PartnerHospitalSelect
            value={hospitalId}
            onChange={setHospitalId}
            variant="portal"
          />
          <Button onClick={requestPayout} className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-500 h-11">
            Request payout ({selected.size} selected)
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => {
            const canSelect = e.status === 'APPROVED' && !e.payoutLine;
            return (
              <label
                key={e.id}
                className={`flex items-center gap-4 rounded-xl border p-4 ${
                  canSelect ? 'border-white/10 bg-white/5 cursor-pointer' : 'border-white/5 bg-white/[0.02] opacity-70'
                }`}
              >
                {canSelect && (
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={() => toggle(e.id)}
                  />
                )}
                <div className="flex-1">
                  <p className="font-medium text-white">{e.lead.patientName}</p>
                  <p className="text-xs text-slate-500">
                    {e.hospital.name} · {e.milestone}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-cyan-400">+{e.points}</p>
                  <p className="text-xs text-slate-500">{e.payoutLine?.payout.status ?? e.status}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
